import classNames from "classnames"

import { Color } from "../../../../constants/V2/color"
import PillButton from "../../../elements/V2/Buttons/PillButton"
import TextButton from "../../../elements/V2/Buttons/TextButton"
import Callout, { CalloutProps } from "../../../elements/V2/Callout"
import GatsbyStoryblokImage from "../../../elements/V2/GatsbyStoryblokImage"
import Grid from "../../../elements/V2/Grid"
import NumberCallout, {
  NumberCalloutProps,
} from "../../../elements/V2/NumberCallout"
import Section from "../../../elements/V2/Section"
import Typography from "../../../elements/V2/Typography"
import Video from "../../../experiments/Video"
import HeroLayout from "../HeroLayout"

import { useElementObserver } from "@hooks/V2/useElementObserver"
import { usePageLayoutState } from "@utils/layout"
import { getBackgroundColorClass, getAccentColor } from "@utils/V2/color"
import React, { ComponentProps, useEffect, useRef } from "react"

type Props = {
  headingText: string
  headingSize?: "display1" | "h1" | "h2" | "h3" | "h4" | "h5"
  headingMobileAlignment?: "left" | "center"
  bodyText: string
  superscriptText?: string
  backgroundColor: Color
  primaryButtonText?: string
  primaryButtonLink?: string | YocoCom.YocoLink
  primaryButtonMobileLink?: string | YocoCom.YocoLink
  primaryButtonBorderColor: Color
  secondaryButtonText?: string
  secondaryButtonLink?: string | YocoCom.YocoLink
  videoUrl?: string
  imageUrl: string
  imageAlt: string
  mobileImageUrl?: string
  mobileImageAlt?: string
  callout?: CalloutProps
  numberCallout?: NumberCalloutProps
  hasScrollArrow?: boolean
  hideMobileStickyButton?: boolean
  trackingEvent?: string
  trackingEventKey?: string
  trackingEventValue?: string
  secondaryTrackingEvent: string
  secondaryTrackingEventKey?: string
  secondaryTrackingEventValue?: string
}

const FloodHero = ({
  headingText,
  headingSize = "h2",
  headingMobileAlignment = "left",
  bodyText,
  superscriptText,
  backgroundColor,
  primaryButtonText,
  primaryButtonLink,
  primaryButtonMobileLink,
  primaryButtonBorderColor,
  secondaryButtonText,
  secondaryButtonLink,
  videoUrl,
  imageUrl,
  imageAlt,
  mobileImageUrl,
  mobileImageAlt,
  callout,
  numberCallout,
  trackingEvent,
  trackingEventKey,
  trackingEventValue,
  hasScrollArrow = false,
  hideMobileStickyButton,
  secondaryTrackingEvent,
  secondaryTrackingEventKey,
  secondaryTrackingEventValue,
  ...props
}: Props) => {
  const accentColor = getAccentColor(backgroundColor)

  const textContainerRef = useRef(null)
  const { isVisible: isCtaVisible, startObserving } =
    useElementObserver(textContainerRef)

  const { setHeroCTAVisible, hasAnchorNavigation } = usePageLayoutState()
  useEffect(() => {
    setHeroCTAVisible(isCtaVisible)

    return () => {
      setHeroCTAVisible(false)
    }
  }, [isCtaVisible, setHeroCTAVisible])

  startObserving()

  const imageProps: Omit<
    ComponentProps<typeof GatsbyStoryblokImage>,
    "image" | "alt"
  > = {
    loadType: "eager",
    quality: 80,
    aspectRatio: "fullWidth",
    className: "sm:min-h-screen sm:w-auto",
  }

  return (
    <HeroLayout
      primaryButtonText={primaryButtonText}
      primaryButtonLink={primaryButtonLink}
      primaryButtonMobileLink={primaryButtonMobileLink}
      primaryButtonColor={primaryButtonBorderColor}
      hideMobileButton={hideMobileStickyButton}
      trackingEvent={trackingEvent}
      trackingEventKey={trackingEventKey}
      trackingEventValue={trackingEventValue}
    >
      <div
        className={classNames(
          "pb-lg -mb-lg relative sm:min-h-screen",
          getBackgroundColorClass(backgroundColor)
        )}
        {...props}
      >
        {videoUrl ? (
          <Video
            video={videoUrl}
            coverImage={imageUrl}
            ImageAlt={imageAlt || "Hero image"}
            loadType="eager"
            aspectRatio="fullWidth"
            playbackMode="autoplay"
            className="sm:min-h-screen sm:w-full"
            playOnMobile={true}
          />
        ) : (
          <>
            <GatsbyStoryblokImage
              {...imageProps}
              image={imageUrl}
              alt={imageAlt || "Hero image"}
              className={classNames(imageProps.className, "hidden sm:block")}
            />
            <div className="relative sm:hidden">
              <GatsbyStoryblokImage
                {...imageProps}
                image={mobileImageUrl || imageUrl}
                alt={mobileImageAlt || imageAlt || "Hero image"}
                className={imageProps.className}
              />
              <div className="absolute bottom-0 h-1/2 w-full bg-gradient-to-b from-transparent to-black" />
            </div>
          </>
        )}

        <Section
          className={classNames(
            "relative left-0 py-32 sm:absolute sm:py-gutter-sm md:py-gutter-md lg:py-gutter-lg xl:py-gutter-xl",
            hasAnchorNavigation
              ? "bottom-60 sm:bottom-auto sm:top-[120px]"
              : "bottom-20"
          )}
        >
          <Grid>
            <div
              ref={textContainerRef}
              className={classNames(
                "col-span-full flex flex-col gap-8 sm:col-span-2 sm:items-start sm:text-left md:col-start-1 md:col-end-4 md:mb-8 lg:col-start-1 lg:col-end-6 lg:mb-16",
                headingMobileAlignment === "left"
                  ? "items-start"
                  : "items-center text-center"
              )}
            >
              {superscriptText && (
                <Typography
                  text={superscriptText}
                  font="grotesk"
                  weight="book"
                  size="subscript-lg"
                  color={accentColor}
                  className="uppercase"
                />
              )}

              {callout && (
                <Callout
                  color={getAccentColor(backgroundColor)}
                  classname="my-16"
                  text={callout.text}
                  highlightedWord={callout.highlightedWord}
                  highlightColor={callout.highlightColor}
                  hasBorder={callout.hasBorder}
                  hasIcon={callout.hasIcon}
                  iconAlt={callout.iconAlt}
                  iconUrl={callout.iconUrl}
                  ctaText={callout.ctaText}
                  ctaLink={callout.ctaLink}
                />
              )}

              <Typography
                text={headingText}
                font="grotesk"
                weight="medium"
                size={headingSize}
                color={accentColor}
                element="h1"
              />
              <Typography
                text={bodyText}
                font="grotesk"
                weight="book"
                size="body-md-fixed"
                color={accentColor}
                className="sm:hidden"
              />
              <Typography
                text={bodyText}
                font="grotesk"
                weight="book"
                size="body-lg-fixed"
                color={accentColor}
                className="hidden sm:block"
              />

              {numberCallout && (
                <NumberCallout
                  icon={numberCallout.icon}
                  text={numberCallout.text}
                  amount={numberCallout.amount}
                  amountPrefix={numberCallout.amountPrefix}
                  amountSuffix={numberCallout.amountSuffix}
                  isAnimated={numberCallout.isAnimated}
                  textColor={accentColor}
                />
              )}
            </div>
            <div className="col-span-full flex flex-col items-center gap-20 sm:flex-row md:gap-24">
              {primaryButtonText && primaryButtonLink && (
                <PillButton
                  size="large"
                  style="solid"
                  text={primaryButtonText}
                  color={primaryButtonBorderColor}
                  linkUrl={primaryButtonLink}
                  mobileLinkUrl={primaryButtonMobileLink}
                  className="w-full sm:w-max"
                  trackingEvent={trackingEvent}
                  trackingEventKey={trackingEventKey}
                  trackingEventValue={trackingEventValue}
                  hasScrollArrow={hasScrollArrow}
                />
              )}

              {secondaryButtonLink && secondaryButtonText && (
                <TextButton
                  style="text"
                  text={secondaryButtonText}
                  linkUrl={secondaryButtonLink}
                  color={accentColor}
                  trackingEvent={secondaryTrackingEvent}
                  trackingEventKey={secondaryTrackingEventKey}
                  trackingEventValue={secondaryTrackingEventValue}
                />
              )}
            </div>
          </Grid>
        </Section>
      </div>
    </HeroLayout>
  )
}

export default FloodHero
